import axios from 'axios';
import config from '../config/config';
import AUTH_CONFIG from '../config/authConfig';
import { shouldAttemptRefresh } from '../utils/cookieUtils';

const BACKEND_BASE_URL = config.backendUrl;
const API_BASE_URL = `${BACKEND_BASE_URL}/api`;
const IMAGE_BASE_URL = config.isDevelopment ? `${BACKEND_BASE_URL}` : `${API_BASE_URL}`;

// Note: With HttpOnly cookies, tokens are managed by the backend
// Frontend no longer has direct access to JWT tokens for security

// Create axios instance with default config for HttpOnly cookies
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Always send cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to get full image URL
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  return `${IMAGE_BASE_URL}${imagePath}`;
};

// Image variants live next to the original with a suffix: moria-5l.jpg -> moria-5l-thmb.jpg
const getImageVariantUrl = (imagePath, suffix) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;

  const pathParts = imagePath.split('.');
  if (pathParts.length >= 2) {
    const extension = pathParts.pop();
    const basePath = pathParts.join('.');
    return `${IMAGE_BASE_URL}${basePath}-${suffix}.${extension}`;
  }

  return `${IMAGE_BASE_URL}${imagePath}`;
};

// Helper function to get thumbnail image URL
export const getThumbnailUrl = (imagePath) => getImageVariantUrl(imagePath, 'thmb');

// Photo cropped to the product on a 3:4 canvas, used only by the product detail page.
// Not every product has one; callers fall back to getImageUrl on load error.
export const getDetailImageUrl = (imagePath) => getImageVariantUrl(imagePath, 'detail');

// Response interceptor: handle 401 and try refresh (simplified for HttpOnly cookies)
let isRefreshing = false;
let failedQueue = [];
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = AUTH_CONFIG.MAX_REFRESH_ATTEMPTS;
const REFRESH_RETRY_DELAY_BASE = AUTH_CONFIG.REFRESH_RETRY_DELAY_BASE;

function processQueue(error, success = false) {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
}

// Exponential backoff delay calculation
function calculateBackoffDelay(attempt) {
  return REFRESH_RETRY_DELAY_BASE * Math.pow(2, attempt - 1) + Math.random() * 1000;
}

// Sleep function for delays
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Enhanced refresh token function with retry logic and exponential backoff
async function attemptTokenRefresh() {
  let lastError = null;
  
  for (let attempt = 1; attempt <= MAX_REFRESH_ATTEMPTS; attempt++) {
    try {
      // With HttpOnly cookies, refresh token is automatically sent
      // Send an empty JSON body to ensure proper Content-Type header
      await api.post('/auth/refresh-token', {});
      
      refreshAttempts = 0; // Reset counter on success
      return true;
    } catch (err) {
      lastError = err;
      
      console.warn(`Token refresh attempt ${attempt}/${MAX_REFRESH_ATTEMPTS} failed:`, {
        status: err.response?.status,
        message: err.message,
        isNetworkError: !err.response
      });
      
      // Don't retry on certain error types
      if (err.response?.status === 401 || err.response?.status === 403) {
        console.log('Token refresh failed with auth error, not retrying');
        break;
      }
      
      // If this isn't the last attempt, wait before retrying
      if (attempt < MAX_REFRESH_ATTEMPTS) {
        const delay = calculateBackoffDelay(attempt);
        console.log(`Waiting ${delay}ms before retry attempt ${attempt + 1}`);
        await sleep(delay);
      }
    }
  }
  
  throw lastError;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Do not redirect if the failed request is the login endpoint or user profile check
    const isLoginRequest = originalRequest && originalRequest.url && originalRequest.url.includes('/auth/login');
    const isProfileRequest = originalRequest && originalRequest.url && originalRequest.url.includes('/users/profile');
    const isRefreshRequest = originalRequest && originalRequest.url && originalRequest.url.includes('/auth/refresh-token');
    
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      !isLoginRequest &&
      !isRefreshRequest
    ) {
      // Check login status cookie before attempting refresh
      // This prevents unnecessary refresh token calls for guest users
      if (!shouldAttemptRefresh()) {
        // console.log('User appears to be guest, skipping refresh token attempt');
        // if (!isProfileRequest) {
        //   window.location.href = '/login';
        // }
        return Promise.reject(new Error('User not authenticated'));
      }

      // Check if we've exceeded global refresh attempts
      if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
        console.error('Maximum refresh attempts exceeded globally, redirecting to login');
        refreshAttempts = 0; // Reset counter
        if (!isProfileRequest) {
          window.location.href = '/login';
        }
        return Promise.reject(new Error(AUTH_CONFIG.ERROR_MESSAGES.MAX_ATTEMPTS_EXCEEDED));
      }
      
      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
        .then(() => {
          // With HttpOnly cookies, retry the original request as-is
          return api(originalRequest);
        })
        .catch(err => Promise.reject(err));
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      refreshAttempts++;
      
      try {
        await attemptTokenRefresh();
        // New tokens are automatically set in HttpOnly cookies by the backend
        processQueue(null, true);
        // Retry the original request - cookies are automatically included
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, false);
        
        // Enhanced error handling based on error type
        const isNetworkError = !refreshError.response;
        const isAuthError = refreshError.response?.status === 401 || refreshError.response?.status === 403;
        
        if (isNetworkError) {
          console.error('Network error during token refresh:', refreshError.message);
          // For network errors, don't redirect immediately - let the user retry
          return Promise.reject(new Error(AUTH_CONFIG.ERROR_MESSAGES.NETWORK_ERROR));
        } else if (isAuthError) {
          console.error('Authentication error during token refresh:', refreshError.response?.status);
          refreshAttempts = 0; // Reset counter on auth failure
          // Only redirect to login if this isn't the initial profile check
          if (!isProfileRequest) {
            window.location.href = '/login';
          }
          return Promise.reject(new Error(AUTH_CONFIG.ERROR_MESSAGES.SESSION_EXPIRED));
        } else {
          console.error('Unexpected error during token refresh:', refreshError);
          return Promise.reject(new Error(AUTH_CONFIG.ERROR_MESSAGES.GENERAL_ERROR));
        }
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// API service class using Axios
class ApiService {
  // Auth APIs
  static async login(email, password) {
    try {
      const response = await api.post('/auth/login', { email, password });
      // With HttpOnly cookies, tokens are automatically set by backend
      // No need to manually store tokens in frontend
      return response.data;
    } catch (error) {
      // Extract backend error message if present
      if (error.response && error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  static async register(firstName, lastName, email, password) {
    try {
      const response = await api.post('/auth/register', {
        firstName,
        lastName,
        name: `${firstName} ${lastName}`.trim(),
        email,
        password
      });
      // With HttpOnly cookies, tokens are automatically set by backend
      return response.data;
    } catch (error) {
      // Extract backend error message if present
      if (error.response && error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  static async logout() {
    // Backend will clear HttpOnly cookies
    await api.post('/auth/logout');
  }

  static async refreshToken() {
    // Check login status before attempting refresh
    if (!shouldAttemptRefresh()) {
      throw new Error('User not logged in, skipping refresh token attempt');
    }
    
    const response = await api.post('/auth/refresh-token', {});
    return response.data;
  }

  static async getUserProfile() {
    const response = await api.get('/users/profile');
    return response.data;
  }

  static async updateUserProfile(userData) {
    const response = await api.put('/users/profile', userData);
    return response.data;
  }

  static async updateProfile(profileData) {
    // Send both firstName and lastName, and also a combined name for backward compatibility
    const data = {
      ...profileData,
      name: `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim(),
    };
    const response = await api.put('/users/profile', data);
    return response.data;
  }

  static async forgotPassword(email) {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  }

  static async resetPassword(token, password) {
    const response = await api.post(`/auth/reset/${token}`, { password });
    return response.data;
  }

  static async changePassword(currentPassword, newPassword) {
    const response = await api.put('/users/profile/change-password', { currentPassword, newPassword });
    return response.data;
  }

  static async verifyEmail(token) {
    const response = await api.get(`/auth/verify-email/${token}`);
    return response.data;
  }

  static async resendVerificationEmail(email) {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  }

  // Order APIs
  static async createOrder(orderData) {
    const response = await api.post('/orders', orderData);
    return response.data;
  }

  static async checkStockAvailability(orderItems) {
    const response = await api.post('/orders/check-stock', { orderItems });
    return response.data;
  }

  static async getOrderById(id) {
    const response = await api.get(`/orders/${id}`);
    return response.data;
  }

  // Members use the authenticated endpoint so an expired token is refreshed
  // instead of silently losing first-order free shipping.
  static async getShippingInfo(isAuthenticated) {
    const response = await api.get(isAuthenticated ? '/orders/shipping-info/me' : '/orders/shipping-info');
    return response.data;
  }

  static async getUserOrders() {
    const response = await api.get('/orders/myorders');
    return response.data;
  }

  static async updateOrder(id, orderData) {
    const response = await api.put(`/orders/${id}`, orderData);
    return response.data;
  }

  static async cancelOrder(id, reason = '') {
    const response = await api.put(`/orders/${id}`, {
      status: 'cancelled',
      notes: reason ? `Cancellation reason: ${reason}` : 'Cancelled by customer'
    });
    return response.data;
  }

  static async createGuestOrder(orderData) {
    // Guest ID is now managed by backend HTTP-only cookies
    const response = await api.post('/orders/guest', orderData);
    return response.data;
  }

  // Admin APIs
  static async getAdminStats() {
    const response = await api.get('/admin/stats');
    return response.data;
  }

  static async getAdminAnalytics(params = {}) {
    const response = await api.get('/admin/analytics', { params });
    return response.data;
  }

  static async getAdminOrders(params = {}) {
    const response = await api.get('/admin/orders', { params });
    return response.data;
  }

  static async getAdminOrderById(id) {
    const response = await api.get(`/admin/orders/${id}`);
    return response.data;
  }

  static async updateAdminOrder(id, orderData) {
    const response = await api.put(`/admin/orders/${id}`, orderData);
    return response.data;
  }

  // Admin User Management APIs
  static async getAdminUsers(params = {}) {
    const response = await api.get('/admin/admin-users', { params });
    return response.data;
  }

  static async getAdminUserById(id) {
    const response = await api.get(`/admin/admin-users/${id}`);
    return response.data;
  }

  static async createAdminUser(userData) {
    const response = await api.post('/admin/admin-users', userData);
    return response.data;
  }

  static async updateAdminUser(id, userData) {
    const response = await api.put(`/admin/admin-users/${id}`, userData);
    return response.data;
  }

  static async deleteAdminUser(id) {
    const response = await api.delete(`/admin/admin-users/${id}`);
    return response.data;
  }

  static async changeAdminPassword(id, passwordData) {
    const response = await api.put(`/admin/admin-users/${id}/change-password`, passwordData);
    return response.data;
  }

  // Customer Management APIs
  static async getCustomers(params = {}) {
    const response = await api.get('/admin/customers', { params });
    return response.data;
  }

  static async getCustomerById(id) {
    const response = await api.get(`/admin/customers/${id}`);
    return response.data;
  }

  static async getAdminProducts(params = {}) {
    const response = await api.get('/admin/products', { params });
    return response.data;
  }

  static async getAdminProductById(id) {
    const response = await api.get(`/admin/products/${id}`);
    return response.data;
  }

  static async createAdminProduct(productData) {
    const response = await api.post('/admin/products', productData);
    return response.data;
  }

  static async updateAdminProduct(id, productData) {
    const response = await api.put(`/admin/products/${id}`, productData);
    return response.data;
  }

  static async deleteAdminProduct(id) {
    const response = await api.delete(`/admin/products/${id}`);
    return response.data;
  }

  // Product APIs
  static async getProductById(id) {
    const response = await api.get(`/products/${id}`);
    return response.data;
  }

  static async getProducts() {
    const response = await api.get('/products');
    return response.data;
  }

  // Cart APIs

  static async getCart(cartId) {
    const response = await api.get(`/cart/${cartId}`);
    return response.data;
  }

  static async getCartFromCookies() {
    const response = await api.get('/cart/from-cookies');
    return response.data;
  }

  static async addItemToCart(productId, quantity) {
    // Always use HTTP-only cookie-based cart management
    try {
      const response = await api.post('/cart/current/add', { productId, quantity });
      return response.data;
    } catch (error) {
      // If guest receives 401, clear cart cookies and retry once
      if (error.response && error.response.status === 401) {
        // Remove cart cookies
        ApiService.clearCartCookies();
        // Retry add-to-cart once
        const retryResponse = await api.post('/cart/current/add', { productId, quantity });
        return retryResponse.data;
      }
      throw error;
    }
  }

  // Utility to clear cart cookies (for browser)
  static clearCartCookies() {
    if (typeof document !== 'undefined') {
      document.cookie = 'mcid=; Max-Age=0; path=/';
      document.cookie = 'mgid=; Max-Age=0; path=/';
    }
  }

  static async removeItemFromCart(productId) {
    // Always use HTTP-only cookie-based cart management
    const response = await api.post('/cart/current/remove', { productId });
    return response.data;
  }

  static async updateCartItem(productId, quantity) {
    // Always use HTTP-only cookie-based cart management
    const response = await api.post('/cart/current/update', { productId, quantity });
    return response.data;
  }

  static async initiatePayment(orderId) {
    const response = await api.post('/payment/initiate', { orderId });
    return response.data;
  }

  // Campaign methods
  static async validateCampaignCode(code, orderTotal) {
    const response = await api.post('/campaigns/validate', { code, orderTotal });
    return response.data;
  }

  static async getAdminCampaigns(params = {}) {
    const response = await api.get('/campaigns', { params });
    return response.data;
  }

  static async getAdminCampaignById(id) {
    const response = await api.get(`/campaigns/${id}`);
    return response.data;
  }

  static async createAdminCampaign(campaignData) {
    const response = await api.post('/campaigns', campaignData);
    return response.data;
  }

  static async updateAdminCampaign(id, campaignData) {
    const response = await api.put(`/campaigns/${id}`, campaignData);
    return response.data;
  }

  static async deleteAdminCampaign(id) {
    const response = await api.delete(`/campaigns/${id}`);
    return response.data;
  }

  static async getAdminCampaignStats(id) {
    const response = await api.get(`/campaigns/${id}/stats`);
    return response.data;
  }

  // Blocklist Management APIs
  static async getBlocklist(params = {}) {
    const response = await api.get('/admin/blocklist', { params });
    return response.data;
  }

  static async addToBlocklist(blockData) {
    const response = await api.post('/admin/blocklist', blockData);
    return response.data;
  }

  static async removeFromBlocklist(id) {
    const response = await api.delete(`/admin/blocklist/${id}`);
    return response.data;
  }

  // Review APIs
  static async getProductReviews(productId, params = {}) {
    const response = await api.get(`/products/${productId}/reviews`, { params });
    return response.data;
  }

  // Review invite tokens travel in a header, never the URL, so they can't
  // end up in nginx or backend access logs.
  static inviteHeaders(inviteToken) {
    return inviteToken ? { 'X-Review-Invite-Token': inviteToken } : {};
  }

  static async getMyProductReview(productId, inviteToken) {
    const response = await api.get(`/products/${productId}/reviews/me`, {
      headers: ApiService.inviteHeaders(inviteToken),
    });
    return response.data;
  }

  static async submitProductReview(productId, { rating, comment, inviteToken }) {
    const response = await api.post(`/products/${productId}/reviews`, {
      rating,
      comment,
      ...(inviteToken ? { inviteToken } : {}),
    });
    return response.data;
  }

  static async deleteMyProductReview(productId, inviteToken) {
    const response = await api.delete(`/products/${productId}/reviews`, {
      headers: ApiService.inviteHeaders(inviteToken),
    });
    return response.data;
  }

  static async getReviewInvite(token) {
    const response = await api.get('/reviews/invite', {
      headers: ApiService.inviteHeaders(token),
    });
    return response.data;
  }

  // Review Moderation APIs
  static async getAdminReviews(params = {}) {
    const response = await api.get('/admin/reviews', { params });
    return response.data;
  }

  static async updateAdminReviewStatus(id, status) {
    const response = await api.patch(`/admin/reviews/${id}`, { status });
    return response.data;
  }

  static async deleteAdminReview(id) {
    const response = await api.delete(`/admin/reviews/${id}`);
    return response.data;
  }
}

export default ApiService;
export { api };